import React, { useState, useCallback, useEffect } from 'react'

import { Modal } from 'almanac-ui/Modal'
import { ModalTrigger } from 'almanac-ui/Modal/ModalTrigger'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { Plus } from 'blaze-ui/icons/20'
import { Browser, Document } from 'blaze-ui/icons'
import { Pdf, Video } from 'blaze-ui/icons/24'
import { Voice, X02 } from 'blaze-ui/icons/16'

import { Source, SourceMaterialsMediaObject, Website, WeekDay } from 'types'
import { CampaignRow, CampaignSource } from './Types'
import { CampaignDatePicker } from './CampaignDatePicker'
import { ThemeLoadingRow } from './ThemeLoadingRow'
import { AddContextModal } from './AddContextModal'

import Styles from './CampaignDetails.module.scss'

const MAX_VISIBLE_SOURCES = 3

const SourcePillIcon = ({ source }: { source: Source }) => {
  if (source.type === 'Website') {
    const website = source as Website
    if (website.favicon) {
      return <img alt="" src={website.favicon} className={Styles.sourcePillFavicon} />
    }
    return <Browser size={12} color="var(--dark-40)" />
  }
  if (source.type === 'SourceMaterialsMediaObject') {
    const media = source as SourceMaterialsMediaObject
    const mediaType = media.mime_type?.split('/')[0]
    const docType = media.mime_type?.split('/')[1] || media.mime_type?.split('.')[1]
    if (mediaType === 'image') return <img alt="" src={media.source_url} className={Styles.sourcePillFavicon} />
    if (mediaType === 'audio') return <Voice size={12} />
    if (mediaType === 'video') return <Video size={12} />
    if (docType === 'pdf') return <Pdf size={12} color="var(--red-50)" />
    return <Document size={12} />
  }
  return <Document size={12} />
}

const getSourceName = (source: Source): string => {
  if (source.type === 'Website') {
    const website = source as Website
    return website.title || website.url
  }
  if (source.type === 'SourceMaterialsMediaObject') {
    const media = source as SourceMaterialsMediaObject
    const parts = media.source_url?.split('/') ?? []
    return parts[parts.length - 1] || media.source_url
  }
  return 'Unknown'
}

const SourcePreviewRow = ({
  sources,
  onRemove,
  addTrigger,
}: {
  sources: CampaignSource[]
  onRemove: (sourceId: number | string) => void
  addTrigger?: React.ReactNode
}) => {
  const visible = sources.slice(0, MAX_VISIBLE_SOURCES)
  const remaining = sources.length - MAX_VISIBLE_SOURCES

  return (
    <div className={Styles.sourcePreview}>
      {visible.map((cs) => (
        <div key={cs.source.id} className={Styles.sourcePill}>
          <SourcePillIcon source={cs.source} />
          <Text variant="placeholder" color="var(--dark-90)" lineClamp={1}>
            {getSourceName(cs.source)}
          </Text>
          <button
            type="button"
            className={Styles.sourcePillRemove}
            onClick={() => onRemove(cs.source.id)}
            aria-label={`Remove ${getSourceName(cs.source)}`}
          >
            <X02 size={10} color="var(--dark-40)" />
          </button>
        </div>
      ))}
      {remaining > 0 && (
        <div className={Styles.sourcePillMore}>
          <Text variant="placeholder" color="var(--dark-60)">
            +{remaining}
          </Text>
        </div>
      )}
      {addTrigger}
    </div>
  )
}

interface CampaignDetailsProps {
  campaigns: CampaignRow[]
  onBack: () => void
  onCreate: (campaigns: CampaignRow[]) => void
  isSubmitting?: boolean
}

export const CampaignDetails = ({ campaigns, onBack, onCreate, isSubmitting = false }: CampaignDetailsProps) => {
  const [editableCampaigns, setEditableCampaigns] = useState<CampaignRow[]>(campaigns)

  useEffect(() => {
    setEditableCampaigns((prev) =>
      campaigns.map((campaign, i) => {
        const existing = prev[i]
        if (!existing) return campaign
        if (existing.loading && !campaign.loading) {
          return { ...existing, theme: campaign.theme, loading: false }
        }
        return existing
      }),
    )
  }, [campaigns])

  const updateDescription = useCallback((index: number, description: string) => {
    setEditableCampaigns((prev) =>
      prev.map((campaign, i) => {
        if (i !== index) return campaign
        return { ...campaign, theme: { ...campaign.theme, description } }
      }),
    )
  }, [])

  const updateDate = useCallback((index: number, startDate: string, endDate: string, length: number) => {
    setEditableCampaigns((prev) =>
      prev.map((campaign, i) => {
        if (i !== index) return campaign
        return { ...campaign, startDate, endDate, length }
      }),
    )
  }, [])

  const updateWeekDays = useCallback((index: number, weekDays: WeekDay[]) => {
    setEditableCampaigns((prev) =>
      prev.map((campaign, i) => {
        if (i !== index) return campaign
        return { ...campaign, weekDays }
      }),
    )
  }, [])

  const updateSources = useCallback((index: number, sources: CampaignSource[]) => {
    setEditableCampaigns((prev) =>
      prev.map((campaign, i) => {
        if (i !== index) return campaign
        return { ...campaign, sources }
      }),
    )
  }, [])

  const removeSource = useCallback((campaignIndex: number, sourceId: number | string) => {
    setEditableCampaigns((prev) =>
      prev.map((campaign, i) => {
        if (i !== campaignIndex) return campaign
        return { ...campaign, sources: campaign.sources.filter((cs) => cs.source.id !== sourceId) }
      }),
    )
  }, [])

  const handleCreate = useCallback(() => {
    onCreate(editableCampaigns)
  }, [editableCampaigns, onCreate])

  const anyLoading = editableCampaigns.some((c) => c.loading)

  return (
    <>
      <Modal.Content className={Styles.content}>
        <div className={Styles.campaignList}>
          <div className={Styles.tableHeader}>
            <span className={Styles.headerLabel}>Campaign Theme</span>
            <span className={Styles.headerLabel}>Timing</span>
          </div>

          {editableCampaigns.map((campaign, index) => (
            <div key={campaign.theme.id} className={Styles.campaignRow}>
              <div className={Styles.themeCol}>
                {campaign.loading ? (
                  <ThemeLoadingRow />
                ) : (
                  <Input
                    isTextarea
                    size="sm"
                    name={`campaign-theme-${index}`}
                    value={campaign.theme.description}
                    onChange={(e) => updateDescription(index, (e.target as HTMLTextAreaElement).value)}
                    placeholder="Describe the campaign theme..."
                    rows={3}
                  />
                )}
                {!campaign.loading && campaign.sources.length > 0 ? (
                  <SourcePreviewRow
                    sources={campaign.sources}
                    onRemove={(sourceId) => removeSource(index, sourceId)}
                    addTrigger={
                      <ModalTrigger
                        modal={AddContextModal}
                        initialSources={campaign.sources}
                        onSave={(sources: CampaignSource[]) => updateSources(index, sources)}
                        options={{ newStack: true }}
                      >
                        <Button
                          variant="secondary"
                          size="sm"
                          frontIcon={Plus}
                          className={Styles.sourcePillAdd}
                          aria-label="Add context"
                        />
                      </ModalTrigger>
                    }
                  />
                ) : (
                  <ModalTrigger
                    modal={AddContextModal}
                    initialSources={campaign.sources}
                    onSave={(sources: CampaignSource[]) => updateSources(index, sources)}
                    options={{ newStack: true }}
                  >
                    <Button variant="ghost" size="sm" frontIcon={Plus} className={Styles.addContextButton}>
                      Add Context
                    </Button>
                  </ModalTrigger>
                )}
              </div>
              <div className={Styles.timingCol}>
                <CampaignDatePicker
                  startDate={campaign.startDate}
                  length={campaign.length}
                  weekDays={campaign.weekDays}
                  onDateChange={(startDate, endDate, length) => updateDate(index, startDate, endDate, length)}
                  onWeekDaysChange={(weekDays) => updateWeekDays(index, weekDays)}
                />
              </div>
            </div>
          ))}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton onPress={onBack} variant="tertiary" size="lg" isDisabled={isSubmitting || anyLoading}>
            Back
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton
            variant="primary"
            size="lg"
            onPress={handleCreate}
            isDisabled={isSubmitting || anyLoading}
          >
            {isSubmitting ? 'Creating...' : 'Create Campaigns'}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
